import type { PropertyDetailListing } from '@/src/components/features/property/types';

export type School = {
  id?: string;
  name: string;
  type: string;
  isPrivate?: boolean;
  lowGrade?: string;
  highGrade?: string;
  rating?: number;
  uriV2: string;
  geo?: { lat: number; lon: number };
};

export type District = {
  id?: string;
  name: string;
  fullName?: string;
  type: string;
  uriV2: string;
};

export type NearbySchool = School & { distance: number };

export type SchoolTypeId = 'all' | 'primaryschool' | 'middleschool' | 'highschool' | 'mixedschool' | 'university';

export const INITIAL_VISIBLE_COUNT = 4;

const SCHOOL_TYPE_ORDER: Array<Exclude<SchoolTypeId, 'all'>> = [
  'primaryschool',
  'middleschool',
  'highschool',
  'mixedschool',
  'university',
];

const SCHOOL_TYPE_LABELS: Record<Exclude<SchoolTypeId, 'all'>, { singular: string; plural: string }> = {
  primaryschool: { singular: 'primary school', plural: 'primary schools' },
  middleschool: { singular: 'middle school', plural: 'middle schools' },
  highschool: { singular: 'high school', plural: 'high schools' },
  mixedschool: { singular: 'mixed school', plural: 'mixed schools' },
  university: { singular: 'university', plural: 'universities' },
};

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const getDistanceMiles = (
  origin: { lat: number; lon: number },
  destination: { lat: number; lon: number },
): number => {
  const earthRadiusMiles = 3958.8;
  const dLat = toRadians(destination.lat - origin.lat);
  const dLon = toRadians(destination.lon - origin.lon);
  const originLat = toRadians(origin.lat);
  const destinationLat = toRadians(destination.lat);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(originLat) * Math.cos(destinationLat) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusMiles * c;
};

export const computeNearestSchools = (
  schools: Array<School>,
  origin: { lat: number; lon: number },
): Array<NearbySchool> =>
  schools
    .filter((school) => school.geo && typeof school.geo.lat === 'number' && typeof school.geo.lon === 'number')
    .map((school) => ({
      ...school,
      distance: Number(getDistanceMiles(origin, school.geo!).toFixed(1)),
    }))
    .sort((a, b) => a.distance - b.distance);

export const groupSchoolsByType = (
  schools: Array<NearbySchool>,
): Record<Exclude<SchoolTypeId, 'all'>, Array<NearbySchool>> => {
  const grouped: Record<Exclude<SchoolTypeId, 'all'>, Array<NearbySchool>> = {
    primaryschool: [],
    middleschool: [],
    highschool: [],
    mixedschool: [],
    university: [],
  };

  for (const school of schools) {
    if (school.type in grouped) {
      grouped[school.type as Exclude<SchoolTypeId, 'all'>].push(school);
    }
  }

  return grouped;
};

export const buildAllTabOrdering = (
  grouped: Record<Exclude<SchoolTypeId, 'all'>, Array<NearbySchool>>,
): { schools: Array<NearbySchool>; firstRestIndex: number } => {
  const firsts: Array<NearbySchool> = [];
  const rest: Array<NearbySchool> = [];

  for (const type of SCHOOL_TYPE_ORDER) {
    const group = grouped[type];
    if (group.length > 0) firsts.push(group[0]);
    for (let i = 1; i < group.length; i += 1) rest.push(group[i]);
  }

  return { schools: [...firsts, ...rest], firstRestIndex: firsts.length };
};

export const getSchoolTypeLabel = (school: Pick<School, 'type' | 'isPrivate'>): string => {
  if (school.type === 'university') return 'College';
  if (school.isPrivate) return 'Private';
  return 'Public';
};

export const getGradeLabel = (lowGrade?: string, highGrade?: string): string | null => {
  if (!lowGrade || lowGrade === 'M') return null;
  const low = lowGrade === 'KG' ? 'K' : lowGrade;
  return `Grades ${low} - ${highGrade ?? ''}`;
};

export const getShowingMessage = (tabId: SchoolTypeId, totalSchools: number, allVisibleCount: number): string => {
  if (totalSchools === 0) return '';
  const initialCount = tabId === 'all' ? allVisibleCount : Math.min(totalSchools, INITIAL_VISIBLE_COUNT);
  if (tabId === 'all') {
    const schoolWord = initialCount === 1 ? 'school' : 'schools';
    return `Showing ${initialCount} of ${totalSchools} ${schoolWord}.`;
  }
  const labels = SCHOOL_TYPE_LABELS[tabId];
  const word = initialCount === 1 ? labels.singular : labels.plural;
  return `Showing ${initialCount} of ${totalSchools} ${word}.`;
};

export const lookupDistrictTypeLabel = (type: string): string | undefined => {
  const labels: Record<string, string> = {
    schooldistrict: 'School District',
    district: 'District',
  };
  return labels[type];
};

export const renderDistrictJoiner = (index: number, total: number): string => {
  const isLast = index === total - 1;
  const isSecondToLast = index === total - 2;
  if (isLast) return '';
  if (total === 2) return ' and ';
  if (total > 2 && isSecondToLast) return ', and ';
  return ', ';
};

export const hasNearbySchoolsData = (listing: PropertyDetailListing | null | undefined): boolean => {
  const schools = (listing?.schools ?? []) as Array<School>;
  const hasListingGeo = typeof listing?.geo?.lat === 'number' && typeof listing?.geo?.lon === 'number';
  const hasGeocodedSchools =
    hasListingGeo &&
    schools.some((school) => school.geo && typeof school.geo.lat === 'number' && typeof school.geo.lon === 'number');
  const districts = ((listing?.areas?.districts ?? []) as Array<District>).filter(
    (district) => district?.uriV2 && district?.name,
  );
  const universities = ((listing?.areas?.schools?.university ?? []) as unknown as Array<School>).filter(
    (school) => school?.uriV2 && school?.name,
  );
  return hasGeocodedSchools || districts.length > 0 || universities.length > 0;
};
